import type { EnvironmentView, Tier } from "@cosmosai/api-client";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { setEnvTier } from "../../app/shell/env-tier";
import { cn } from "../../ui/cn";
import { EnvBadge } from "../../ui/env-badge";
import { useCosmosContext, useEnvironment } from "./use-context";

// The mock's .env-switch colours, by tier: a dot, and the pressed segment.
const dotClass: Record<Tier, string> = {
  development: "bg-dev",
  staging: "bg-uat",
  production: "bg-gold",
};
const pressedClass: Record<Tier, string> = {
  development: "aria-pressed:bg-dev-soft aria-pressed:text-dev",
  staging: "aria-pressed:bg-uat-soft aria-pressed:text-uat",
  production: "aria-pressed:bg-prod aria-pressed:text-prod-ink",
};

function Switch({ workspace }: { workspace: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { environments, current } = useEnvironment(workspace);
  const tier = current?.tier ?? null;
  // data-env colours the top bar and shows the production banner; leaving a workspace clears it.
  useEffect(() => {
    setEnvTier(tier);
    return () => setEnvTier(null);
  }, [tier]);
  if (!current) return null;
  const choose = (env: EnvironmentView) =>
    void navigate({ to: ".", search: (prev: object) => ({ ...prev, env: env.name }) } as never);
  if (environments.length === 1) {
    return <EnvBadge tier={current.tier} name={current.name} className="uppercase" />;
  }
  return (
    <fieldset className="inline-flex flex-none gap-0.5 rounded-[10px] border border-line bg-surface-2 p-[3px]">
      <legend className="sr-only">{t("context.environment")}</legend>
      {environments.map((env) => (
        <button
          key={env.name}
          type="button"
          data-tier={env.tier}
          aria-pressed={env.name === current.name}
          onClick={() => choose(env)}
          className={cn(
            "flex h-7 items-center gap-1.5 rounded-[7px] px-[11px] text-sm font-bold tracking-[0.02em] text-ink-2 uppercase hover:bg-hover hover:text-ink max-md:px-2",
            pressedClass[env.tier],
          )}
        >
          <span
            aria-hidden
            className={cn("size-[7px] rounded-full max-md:hidden", dotClass[env.tier])}
          />
          {env.name}
        </button>
      ))}
    </fieldset>
  );
}

/** The top bar's environment switch: the `env` search parameter, coloured by tier. */
export function EnvSwitch() {
  const { workspace } = useCosmosContext();
  return workspace ? <Switch workspace={workspace} /> : null;
}
